/* eslint-disable eqeqeq */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, {
  forwardRef,
  useState,
  useImperativeHandle,
  useRef,
  useEffect,
} from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import Select from '../../atoms/select/select';
import Badge from '../../atoms/badge/badge';
import FormValidation from '../../atoms/formEnums/formValidation';
import Label from '../../atoms/label/label';
import { IconType } from '../../atoms/icon/icon';
import AsyncSelect from '../../atoms/asynSelect/asyncSelect';

/**
 * Form select is a multiple select input designed to work inside a form component.
 * It contains a label, HelpButton, Select and badges.
 * The multiple select is saved on key array and it’s sent to the parent component.
 */
const FormSelect = forwardRef((props, ref) => {
  const {
    id, options, label, validation, errorText, helpText, multiple, value, onChange, 
    asyncSelect, placeholder, url,validateUrl, session, filter, fieldValue, fieldName, 
    emptyValue, pageId, disabled, parentField, refreshData, allValue, writable, fieldDisabled, fieldLabelKey,
    fieldDisabledReason,noOptionsMessageText, loadInitialWithoutParent, customStyles, ignoreEmptyValue, urlByField,sessionParams, fieldCode} = props;

  const currentOptions = typeof options == 'string'
    ? useSelector((state) => [{ value: '', name: '' }]
      .concat(state[options]
        .map((option) => ({ value: option.id, name: option.name,disabled:(option.disabled?'disabled':'') })))) : options;

  const childRef = useRef();
  const [valueState, setValueState] = useState();

  useImperativeHandle(ref, () => ({
    updateValue(newValue) {
      setValueState(newValue);
    },
  }));

  useEffect(() => {
    // console.log("value change",value,valueState);
    // console.log("ssessionParams",sessionParams);
    if (value !== valueState) {
      if(multiple){
        const newValue = Array.isArray(value) ? value : value != "" ? value.split(',') : [];
        setValueState(newValue);
      } else {
        setValueState(value);
      }

      childRef.current.updateValue(value);
    }

  }, [value]);
  
  useEffect(() => {
    if(emptyValue && currentOptions){
        if(currentOptions.find(element => element.value == '(Empty)')==undefined){
          currentOptions.unshift({"value":'(Empty)',"name":'(no selection)'});
        }
    }
  }, []);

 
  const onChangeValue = (newValue) => {
    if (multiple && !asyncSelect) {
      const newState = [...valueState];
      if(newState.find(element => element == newValue.target.value) == undefined){
        newState.push(newValue.target.value);
        setValueState(newState);
        onChange({ ...newValue, target: { value: newState.join() } });
      }
    } else if (multiple && asyncSelect){
      if(validateUrl){
      }else{
        const newValues = newValue.map(item=>typeof item == "string" ? item : (item[fieldLabelKey] ?? item.code)).join();
        setValueState( newValues.split(','));
        if(newValues==""){
          onChange({target: { value: null } });
          setValueState([]);
        }
        else
          onChange({target: { value: newValues } });
      }
    } else {
      setValueState(newValue.target.value);
      onChange(newValue);
    }
  };

  const onClickBadge = (event, valueStateIndex) => {
    const newState = [...valueState];
    newState.splice(valueStateIndex, 1);
    setValueState(newState);
    onChange({ ...event, target: { value: newState.join() } });
  };

  const computedUrl = (() => {
    if(url){
      if (!urlByField) return url;
      const parentValue = refreshData;
      if (!parentValue || !urlByField.map) return url;
      return urlByField.map[parentValue] || url;
    }  
  })();
  return (
    <div className={`form-group row text-${validation}`}>
      { label !== ''
        && (
          <Label helpText={helpText}>{label}</Label>
        )}
    <div  className={`${multiple === true ? 'col-sm-8' : 'col'}`}>
      { multiple
        && (
        <span className={`col-form-label ${label === '' ? 'col' : ''}`}>
          { typeof valueState === 'object'
          && (valueState.map((val, i) => {
            if (val !== undefined && currentOptions
              .find((option) => option.value == val) !== undefined) {
              return (
                <Badge
                  key={val}
                  icon={IconType.CLOSE}
                  onClick={(event) => onClickBadge(event, i)}
                >
                  {currentOptions.find((option) => option.value == val).name}
                </Badge>
              );
            }
            return null;
          }))}
        </span>
        )}
      <div className={!multiple ? '' : ''} id={id+'--main'}>
        {
          asyncSelect
          && (
            <AsyncSelect
            id={id}
            ref={childRef}
            multiple={multiple}
            options={currentOptions}
            validation={validation}
            parentField={parentField}
            refreshData={refreshData}
            onChange={onChangeValue}
            session={session}
            filter={filter}
            url={computedUrl}
            fieldValue={fieldValue}
            fieldName={fieldName}
            fieldCode={fieldCode}
            fieldLabelKey={fieldLabelKey}
            placeholder={placeholder}
            emptyValue={emptyValue}
            allValue={allValue}
            value={valueState}
            pageId={pageId}
            disabled={disabled}
            writable={writable}
            fieldDisabled={fieldDisabled}
            fieldDisabledReason={fieldDisabledReason}
            loadInitialWithoutParent={loadInitialWithoutParent}
            noOptionsMessageText={noOptionsMessageText}
            customStyles={customStyles}
            ignoreEmptyValue={ignoreEmptyValue}
            sessionParams={sessionParams?sessionParams:[]}
          />
          )
        }
        {
          !asyncSelect
          && (
            <Select
            id={id}
            ref={childRef}
            options={currentOptions}
            validation={validation}
            onChange={onChangeValue}
            emptyValue={emptyValue}
            value={valueState && multiple === false ? valueState : ''}
            disabled={disabled}
            writable={writable}
          />
          )
        }

        { (validation === FormValidation.ERROR || validation === FormValidation.WARNING)
          && (
            <small className="form-text">{errorText}</small>
          )}
      </div>
      </div>
    </div>
  );
});

FormSelect.propTypes = {
  /**
   * Id of select input
   */
  id: PropTypes.string,
  /**
   * Select options defined by name and value
   */
  options: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  })), PropTypes.string]).isRequired,
  /**
   * Name of select attribute
   */
  label: PropTypes.string,
  /**
   * Defined text errors
   */
  errorText: PropTypes.string,
  /**
   * Help text
   */
  helpText: PropTypes.string,
  /**
   * FormSelect form status, it is a FormValidation enum
   */
  validation: PropTypes.oneOf(Object.values(FormValidation)),
  /**
 * Initial select value
 */
  value: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    PropTypes.string,
    PropTypes.number,
  ]),
  /**
   * To use the form select with multiple options or single options
   */
  multiple: PropTypes.bool,
  /**
   * Function to bind onChange HTML functionality
   */
  onChange: PropTypes.func,
};

FormSelect.defaultProps = {
  id: '',
  label: '',
  validation: FormValidation.PENDING,
  errorText: '',
  helpText: '',
  value: '',
  multiple: false,
  loadInitialWithoutParent: false,
  ignoreEmptyValue: false,
  urlByField: null,  
  fieldCode: '',
  fieldLabelKey: '',
  onChange: () => {},
};

export default FormSelect;
